import { LockIcon } from "@admin/pages/objects-configure/assets/Icon";
import { List as AntdList, Spin, Tag, Typography } from "antd";
import { useEffect } from "react";

import { EAZY_CONSTANTS } from "../constants";
import { formatDeviceContractForDisplay } from "../utils/deviceContract";

const selectedItemStyle: React.CSSProperties = {
  border: "2px solid #1890ff",
  borderTop: "2px solid #1890ff",
  backgroundColor: "#E6F7FF",
};

const { Text } = Typography;

export interface Address {
  full_address?: string;
  prefecture_name: string;
  city_name: string;
  postal_code?: string;
  district_name?: string;
  banchi?: string;
  no?: string;
  chiban_number?: string;
  additional?: string;
  longitude?: number;
  latitude?: number;
}

export interface BuildingSearchProperty {
  id: string;
  device_contracts: string[];
}

export type EazySyncStatus = "CREATED" | "CREATE_FAILED" | "UPDATED" | "UPDATE_FAILED" | "DELETED";

export interface EazySyncInfo {
  management_id?: string;
  device_serial_id?: string;
  status: EazySyncStatus;
  address?: string;
  model?: string;
}

export interface BuildingItem {
  building_id: string;
  name: string;
  address?: Address;
  property: BuildingSearchProperty;
  eazy_sync?: EazySyncInfo;
}

export interface BuildingSearchResult {
  total: number;
  total_pages?: number;
  items: BuildingItem[];
}

interface BuildingSearchResultsProps {
  results: BuildingItem[];
  onDragStart: (item: BuildingItem) => void;
  onBuildingClick?: (item: BuildingItem) => void;
  selectedBuildingId?: string;
  onLoadMore?: () => Promise<void>;
  loadingMore?: boolean;
  containerRef?: React.RefObject<HTMLDivElement>;
}

export const BuildingSearchResults = ({
  results,
  onDragStart,
  onBuildingClick,
  selectedBuildingId,
  onLoadMore,
  loadingMore = false,
  containerRef,
}: BuildingSearchResultsProps) => {
  const handleDragStart = (e: React.DragEvent, item: BuildingItem) => {
    e.dataTransfer.setData("text/plain", JSON.stringify(item));
    e.dataTransfer.effectAllowed = "move";
    onDragStart(item);
  };

  const handleClick = (item: BuildingItem) => {
    onBuildingClick?.(item);
  };

  useEffect(() => {
    if (!onLoadMore || !containerRef?.current) return;

    const container = containerRef.current;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < EAZY_CONSTANTS.SCROLL_THRESHOLD && !loadingMore && onLoadMore) {
        onLoadMore();
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [onLoadMore, containerRef, loadingMore]);

  const getStatusTag = (status?: EazySyncInfo["status"]) => {
    if (!status) return null;

    if (status === "CREATED" || status === "UPDATED" || status === "UPDATE_FAILED") {
      return (
        <Tag bordered={false} color="processing">
          登録済
        </Tag>
      );
    }

    if (status === "DELETED") {
      return (
        <Tag bordered={false} color="error">
          削除済
        </Tag>
      );
    }

    return null;
  };

  return (
    <div style={{ width: "100%", padding: "0 16px" }}>
      <AntdList
        dataSource={results}
        renderItem={(item) => {
          const isSelected = selectedBuildingId === item.building_id;
          return (
            <AntdList.Item
              draggable={!onBuildingClick}
              onClick={onBuildingClick ? () => handleClick(item) : undefined}
              onDragStart={(e) => handleDragStart(e, item)}
              style={
                isSelected
                  ? {
                    cursor: onBuildingClick ? "pointer" : "move",
                    padding: "16px",
                    borderRadius: "4px",
                    marginBottom: "8px",
                    userSelect: "none",
                    height: "112px",
                    width: "100%",
                    ...selectedItemStyle,
                  }
                  : {
                    cursor: onBuildingClick ? "pointer" : "move",
                    padding: "16px",
                    border: "1px solid #d9d9d9",
                    borderTop: "1px solid #E5E7EB",
                    borderRadius: "4px",
                    marginBottom: "8px",
                    userSelect: "none",
                    height: "112px",
                    width: "100%",
                    backgroundColor: "transparent",
                  }
              }
            >
              <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "8px" }}>
                <Text
                  style={{
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "22px",
                    color: "#000000",
                  }}
                >
                  {item.name}
                </Text>
                {item.property.device_contracts[0] && (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <LockIcon height="14" width="14" />
                    <Text
                      style={{
                        color: "#6A7282",
                        fontWeight: 400,
                        fontSize: "13px",
                        lineHeight: "22px",
                      }}
                    >
                      {formatDeviceContractForDisplay(item.property.device_contracts[0])}
                    </Text>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Text
                    style={{
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "20px",
                    }}
                  >
                    棟ID: {item.building_id}
                  </Text>
                  {getStatusTag(item.eazy_sync?.status)}
                </div>
              </div>
            </AntdList.Item>
          );
        }}
      />
      {loadingMore && (
        <div style={{ display: "flex", justifyContent: "center", padding: "16px" }}>
          <Spin />
        </div>
      )}
    </div>
  );
};
